import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Users, FileText, Pill, AlertCircle, MessageSquare, Send, LogIn, ShieldAlert } from "lucide-react";

export default function PlatformAgencyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [posting, setPosting] = useState(false);
  const [impersonating, setImpersonating] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/platform/agencies/${id}`)
      .then((r) => setData(r.data))
      .catch(() => { toast.error("Could not load agency"); navigate("/platform/agencies"); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [id]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setPosting(true);
    try {
      await api.post(`/platform/agencies/${id}/notes`, { body: note.trim() });
      setNote("");
      toast.success("Note saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save note");
    } finally {
      setPosting(false);
    }
  };

  const startImpersonation = async () => {
    if (!window.confirm(
      "You're about to impersonate this agency's admin. All actions will be audit-logged. Continue?"
    )) return;
    setImpersonating(true);
    try {
      const { data: r } = await api.post(`/platform/agencies/${id}/impersonate`);
      toast.success(`Now viewing as ${r.as_email}`);
      window.location.href = "/admin";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impersonation failed");
      setImpersonating(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-white/60">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading agency…
    </div>
  );
  if (!data) return null;

  const a = data.agency;
  const maxDaily = Math.max(1, ...data.reports_14d.map((d) => d.count));

  return (
    <div className="space-y-8" data-testid="platform-agency-detail">
      <Link to="/platform/agencies" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> All agencies
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-amber-300/80 font-bold">Agency</p>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">{a.name}</h1>
          <div className="mt-1 text-sm text-white/50 font-mono">{a.slug} · {a.id?.slice(0, 8)}</div>
          {a.created_at && (
            <div className="mt-1 text-xs text-white/40">Joined {new Date(a.created_at).toLocaleDateString()}</div>
          )}
        </div>
        <button
          type="button"
          onClick={startImpersonation}
          disabled={impersonating}
          className="bg-amber-300 hover:bg-amber-200 text-amber-950 rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="impersonate-button"
        >
          {impersonating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          Log in as this agency
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Users" value={data.users.length} sub={`${data.users.filter((u) => u.role === "caregiver").length} caregivers`} />
        <Stat icon={Pill} label="Clients" value={data.clients_recent.length + "+"} sub="active" />
        <Stat icon={FileText} label="Invoices" value={data.invoices.total} sub={`${data.invoices.paid} paid`} />
        <Stat icon={AlertCircle} label="Incidents" value={data.reports_recent.filter((r) => r.incident_flag).length} sub="recent" tone="bg-rose-400/10 text-rose-300" />
      </div>

      {/* 14-day report chart */}
      <section className="rounded-xl bg-white/5 border border-white/10 p-5">
        <h2 className="font-heading text-base font-medium mb-3 text-white/90">Reports submitted — last 14 days</h2>
        <div className="flex items-end gap-1.5 h-32">
          {data.reports_14d.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1">
              <div
                className="w-full bg-amber-300/80 rounded-t hover:bg-amber-200 transition-colors"
                style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? "3px" : "0" }}
                title={`${d.date}: ${d.count} report${d.count !== 1 ? "s" : ""}`}
              />
              <span className="text-[9px] text-white/40 tabular-nums">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Users */}
        <section className="rounded-xl bg-white/5 border border-white/10 p-5">
          <h2 className="font-heading text-base font-medium mb-3 text-white/90 flex items-center gap-2">
            <Users className="h-4 w-4 text-amber-300" /> Team
          </h2>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {data.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
                <div className="min-w-0">
                  <div className="text-white truncate">{u.name}</div>
                  <div className="text-[11px] text-white/40 truncate">{u.email}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wide font-semibold ${u.role === "admin" ? "text-amber-200" : "text-sky-200"}`}>
                  {u.role}
                </span>
              </div>
            ))}
            {data.users.length === 0 && <div className="text-sm text-white/40 italic">No users.</div>}
          </div>
        </section>

        {/* Recent reports */}
        <section className="rounded-xl bg-white/5 border border-white/10 p-5">
          <h2 className="font-heading text-base font-medium mb-3 text-white/90 flex items-center gap-2">
            <FileText className="h-4 w-4 text-amber-300" /> Recent reports
          </h2>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {data.reports_recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
                <div className="min-w-0">
                  <div className="text-white truncate">{r.caregiver_name}</div>
                  <div className="text-[11px] text-white/40">{r.shift_date}</div>
                </div>
                {r.incident_flag && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-rose-300 inline-flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" /> Incident
                  </span>
                )}
              </div>
            ))}
            {data.reports_recent.length === 0 && <div className="text-sm text-white/40 italic">No reports yet.</div>}
          </div>
        </section>
      </div>

      {/* Internal notes (CRM-style) */}
      <section className="rounded-xl bg-white/5 border border-white/10 p-5">
        <h2 className="font-heading text-base font-medium mb-3 text-white/90 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-amber-300" /> Internal notes
        </h2>
        <form onSubmit={addNote} className="flex gap-2 mb-4">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (e.g. demo scheduled, pricing tier, escalation contact)…"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-amber-300 focus:outline-none"
            data-testid="note-input"
          />
          <button
            type="submit"
            disabled={posting || !note.trim()}
            className="bg-amber-300 hover:bg-amber-200 text-amber-950 rounded-lg px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="note-add-button"
          >
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Add
          </button>
        </form>
        {data.notes.length === 0 ? (
          <div className="text-sm text-white/40 italic">No notes yet. Notes are private to platform admins.</div>
        ) : (
          <ul className="space-y-2">
            {data.notes.map((n) => (
              <li key={n.id} className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
                <div className="text-sm text-white/90 whitespace-pre-wrap">{n.body}</div>
                <div className="mt-1 text-[11px] text-white/40">
                  {n.author_email} · {new Date(n.created_at).toLocaleString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Audit log */}
      <section className="rounded-xl bg-white/5 border border-white/10 p-5">
        <h2 className="font-heading text-base font-medium mb-3 text-white/90">Platform actions on this agency</h2>
        {data.audit_log.length === 0 ? (
          <div className="text-sm text-white/40 italic">No platform actions yet.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.audit_log.map((e) => (
              <div key={e.id} className="grid grid-cols-12 gap-3 py-2 text-sm">
                <span className="col-span-3 text-white/50 tabular-nums text-xs">
                  {new Date(e.created_at).toLocaleString()}
                </span>
                <span className="col-span-3 text-amber-200">{e.action}</span>
                <span className="col-span-3 text-white/80 truncate">{e.actor_email}</span>
                <span className="col-span-3 text-white/40 truncate">{e.ip}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, tone = "bg-amber-400/10 text-amber-300" }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 p-4">
      <div className={`inline-flex items-center justify-center h-7 w-7 rounded-lg ${tone} mb-2`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="text-2xl font-heading font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-white/40 mt-1">{label}</div>
      <div className="text-xs text-white/60 mt-1">{sub}</div>
    </div>
  );
}
